/**
 * Size Units Toggle with Automatic Conversion
 * Converts CM sizes to IN sizes when toggled (single set of sizes from Shopify)
 */

(function() {
  'use strict';

  let currentActiveUnit = 'cm';
  let originalSizes = {};

  function log(message, data) {
    console.log(`[size-units] ${message}`, data || '');
  }

  function cmToInches(cm) {
    // More precise conversion using fractional inches
    const conversionMap = {
      35: '13¾',  // 13.78 -> 13¾
      36: '14',    // 14.17 -> 14
      37: '14½',   // 14.57 -> 14½
      38: '15',    // 14.96 -> 15
      39: '15½',   // 15.35 -> 15½
      40: '15¾',   // 15.75 -> 15¾
      41: '16',    // 16.14 -> 16
      42: '16½',   // 16.54 -> 16½
      43: '17',    // 16.93 -> 17
      44: '17½',   // 17.32 -> 17½
      45: '17¾',   // 17.72 -> 17¾
      46: '18'     // 18.11 -> 18
    };
    
    // Use the exact conversion map if available, otherwise calculate and format as fraction
    if (conversionMap[cm]) {
      return conversionMap[cm];
    }
    
    // For sizes not in the map, calculate and convert to fractional format
    const inches = cm / 2.54;
    return formatToFractionalInches(inches);
  }

  function formatToFractionalInches(inches) {
    const whole = Math.floor(inches);
    const decimal = inches - whole;
    
    // Convert decimal to closest fraction
    if (decimal < 0.125) {
      return whole.toString();
    } else if (decimal < 0.375) {
      return whole + '¼';
    } else if (decimal < 0.625) {
      return whole + '½';
    } else if (decimal < 0.875) {
      return whole + '¾';
    } else {
      return (whole + 1).toString();
    }
  }

  function storeOriginalSizes(scope) {
    // Find all size labels in the variant picker
    const sizeLabels = scope.querySelectorAll('.product-option__label span, label span');
    
    sizeLabels.forEach(label => {
      const text = label.textContent.trim();
      const size = parseInt(text);
      
      if (!isNaN(size) && size > 0 && size >= 30 && size <= 50) { // Only size numbers
        // Store original CM value
        if (!originalSizes[size]) {
          originalSizes[size] = {
            cm: size,
            in: cmToInches(size)
          };
        }
        
        // Store reference to the label
        label.setAttribute('data-original-cm', size);
        label.setAttribute('data-converted-in', originalSizes[size].in);
      }
    });
    
    log('Stored original sizes:', originalSizes);
  }

  function convertSizesToUnit(scope, unit) {
    const sizeLabels = scope.querySelectorAll('.product-option__label span, label span');
    
    log(`Converting sizes to unit: ${unit}, found ${sizeLabels.length} labels`);
    
    sizeLabels.forEach(label => {
      const originalCm = label.getAttribute('data-original-cm');
      const convertedIn = label.getAttribute('data-converted-in');
      
      log(`Label: ${label.textContent}, originalCm: ${originalCm}, convertedIn: ${convertedIn}`);
      
      if (originalCm && convertedIn) {
        if (unit === 'cm') {
          // Show original CM value
          label.textContent = originalCm;
          log(`Showing CM: ${originalCm}`);
        } else {
          // Show converted IN value
          label.textContent = convertedIn;
          log(`Showing IN: ${convertedIn}`);
        }
        
        // Update the corresponding input value to match the displayed value
        const labelElement = label.closest('label');
        if (labelElement) {
          const input = labelElement.querySelector('input[type="radio"]');
          if (input) {
            // Store the original value
            if (!input.getAttribute('data-original-value')) {
              input.setAttribute('data-original-value', input.value);
            }
            
            // Set the input value to match what's displayed
            if (unit === 'cm') {
              input.value = originalCm;
      } else {
              input.value = convertedIn;
            }
            
            log(`Updated input value: ${input.value} (unit: ${unit})`);
          }
        }
      }
    });
  }

  function createToggle(scope) {
    // Always remove existing toggle first to prevent duplicates
    const existingToggle = scope.querySelector('.size-unit-selector');
    if (existingToggle) {
      existingToggle.remove();
    }

    // Create the toggle HTML with label
    const toggleHTML = `
      <div class="size-unit-selector">
        <label class="size-unit-label">Size in:</label>
        <div class="unit-toggle" data-unit-toggle>
          <button type="button" class="button button--text" data-unit="cm" aria-pressed="true">CM</button>
          <button type="button" class="button button--text" data-unit="in" aria-pressed="false">IN</button>
        </div>
      </div>
    `;
    
    // Insert the toggle before the first fieldset
    const firstFieldset = scope.querySelector('fieldset');
    if (firstFieldset) {
      firstFieldset.insertAdjacentHTML('beforebegin', toggleHTML);
      log('Created toggle');
      
      // Add event listener to the new toggle
      const newToggle = scope.querySelector('[data-unit-toggle]');
      if (newToggle) {
        newToggle.addEventListener('click', handleToggleClick);
      }
      
      return newToggle;
    }
    
    return null;
  }

  function setActiveUnit(scope, unit) {
    const toggle = scope.querySelector('[data-unit-toggle]');
    if (!toggle) return;

    // Update toggle buttons
    const buttons = toggle.querySelectorAll('[data-unit]');
    buttons.forEach(btn => {
      const isActive = btn.getAttribute('data-unit') === unit;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    // Update scope attribute
    scope.setAttribute('data-active-unit', unit);
    currentActiveUnit = unit;
    
    log(`Set active unit to: ${unit}`);
  }

  function handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target.closest('[data-unit]');
    if (!button) return;

    const unit = button.getAttribute('data-unit');
    const scope = button.closest('product-variant-selects');
    
    if (!scope) return;

    log(`Toggle clicked: ${unit}`);
    
    // Set the active unit
    setActiveUnit(scope, unit);
    
    // Convert the sizes to the selected unit
    convertSizesToUnit(scope, unit);
    
    // Store preference
    localStorage.setItem('size-unit-preference', unit);
    
    log(`Converted sizes to: ${unit}`);
  }

  function initializeScope(scope) {
    if (scope.hasAttribute('data-size-units-initialized')) return;
    
    log('Initializing scope:', scope);

    // Create the toggle
    const toggle = createToggle(scope);
    
    // Store original sizes for conversion
    storeOriginalSizes(scope);
    
    // Set initial active unit (default to CM)
    const storedUnit = localStorage.getItem('size-unit-preference') || 'cm';
    setActiveUnit(scope, storedUnit);
    
    // Convert to the stored unit
    convertSizesToUnit(scope, storedUnit);
    
    // Add event listeners
    if (toggle) {
      toggle.addEventListener('click', handleToggleClick);
    }
    
    // Mark as initialized
    scope.setAttribute('data-size-units-initialized', 'true');
    
    log('Scope initialized with conversion logic');
  }

  function init() {
    log('Starting initialization...');

    // Find all variant picker scopes
    const scopes = document.querySelectorAll('product-variant-selects');
    
    if (scopes.length === 0) {
      log('No variant picker scopes found');
      return;
    }

    // Initialize each scope
    scopes.forEach(scope => {
      initializeScope(scope);
    });

    log('Initialization complete - sizes will convert on toggle');
  }

  // Re-initialize after AJAX updates
  function reinitializeAfterAjax() {
    log('Re-initializing after AJAX...');
    
    // Re-run initialization for any new elements
    const scopes = document.querySelectorAll('product-variant-selects:not([data-size-units-initialized])');
    scopes.forEach(scope => {
      initializeScope(scope);
    });
    
    // Re-apply conversion for existing scopes and ensure toggle exists
    const allScopes = document.querySelectorAll('product-variant-selects');
    allScopes.forEach(scope => {
      // Always recreate toggle to ensure it exists after AJAX
      const toggle = createToggle(scope);
      
      // Store original sizes and apply conversion
      storeOriginalSizes(scope);
      
      // Use stored preference instead of scope attribute (which gets reset)
      const storedUnit = localStorage.getItem('size-unit-preference') || 'cm';
      setActiveUnit(scope, storedUnit);
      convertSizesToUnit(scope, storedUnit);
      
      log(`Reinitialized with stored unit: ${storedUnit}`);
    });
  }

  // Hook into Shopify's variant updates
  function hookIntoVariantUpdates() {
    // Override the updateOptionValues method
    if (window.ProductInfo && window.ProductInfo.prototype && window.ProductInfo.prototype.updateOptionValues) {
      const originalUpdateOptionValues = window.ProductInfo.prototype.updateOptionValues;
      
      window.ProductInfo.prototype.updateOptionValues = function(...args) {
        const result = originalUpdateOptionValues.apply(this, args);
        
        // Re-initialize after variant update with multiple attempts
        setTimeout(() => reinitializeAfterAjax(), 50);
        setTimeout(() => reinitializeAfterAjax(), 150);
        setTimeout(() => reinitializeAfterAjax(), 300);
        
        return result;
      };
      
      log('Hooked into ProductInfo.updateOptionValues');
    }

    // Also intercept fetch requests for variant updates and cart additions
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [url, options] = args;
      
      // Check if this is a cart/add request
      if (typeof url === 'string' && url.includes('/cart/add')) {
        log('Detected cart add request');
        
        // Get the current active unit
        const scope = document.querySelector('product-variant-selects');
        const activeUnit = scope ? scope.getAttribute('data-active-unit') || 'cm' : 'cm';
        
        if (activeUnit === 'in' && options && options.body) {
          try {
            // Parse the form data
            const formData = new URLSearchParams(options.body);
            const sizeValue = formData.get('properties[Size]') || formData.get('properties[size]');
            
            if (sizeValue) {
              log(`Original size value in cart request: ${sizeValue}`);
              
              // Find the converted value
              const convertedValue = Object.keys(originalSizes).find(cm => 
                originalSizes[cm].cm.toString() === sizeValue
              );
              
              if (convertedValue && originalSizes[convertedValue]) {
                const newSizeValue = originalSizes[convertedValue].in; // Already a string with fractions
                formData.set('properties[Size]', newSizeValue);
                formData.set('properties[size]', newSizeValue);
                
                options.body = formData.toString();
                log(`Updated cart request size: ${sizeValue} -> ${newSizeValue}`);
                
                // Store the conversion for later cart display updates
                window.lastCartConversion = {
                  original: sizeValue,
                  converted: newSizeValue,
                  unit: 'in'
                };
              }
            }
          } catch (e) {
            log('Error processing cart request:', e);
          }
        }
      }
      
      // Check if this is a variant-related request
      if (typeof url === 'string' && (url.includes('variant=') || url.includes('variant_id='))) {
        log('Detected variant fetch request');
        
        return originalFetch.apply(this, args).then(response => {
          // Re-initialize after fetch completes
          setTimeout(() => {
            reinitializeAfterAjax();
          }, 150);
          
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };
    
    log('Hooked into fetch for variant requests');
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run on window load as backup
  window.addEventListener('load', () => {
    init();
  });

  // Form submission handler to ensure correct values are sent
  function handleFormSubmission(event) {
    const form = event.target;
    if (!form || !form.querySelector('product-variant-selects')) return;
    
    const scope = form.querySelector('product-variant-selects');
    const activeUnit = scope.getAttribute('data-active-unit') || 'cm';
    
    log(`Form submission - active unit: ${activeUnit}`);
    
    // Find the selected size input
    const selectedInput = scope.querySelector('input[type="radio"]:checked');
    if (selectedInput) {
      const originalValue = selectedInput.getAttribute('data-original-value');
      const displayedValue = selectedInput.value;
      
      log(`Selected input - original: ${originalValue}, displayed: ${displayedValue}`);
      
      // If we're in IN mode, we need to ensure the form data reflects the converted value
      if (activeUnit === 'in') {
        // Update the input's value attribute and any hidden inputs
        selectedInput.value = displayedValue;
        
        // Also update any hidden inputs that might contain the variant data
        const hiddenInputs = form.querySelectorAll('input[type="hidden"]');
        hiddenInputs.forEach(input => {
          if (input.name && input.name.includes('properties[') && input.value === originalValue) {
            input.value = displayedValue;
            log(`Updated hidden input ${input.name}: ${originalValue} -> ${displayedValue}`);
          }
        });
        
        log(`Sending converted value to cart: ${displayedValue}`);
      } else {
        // In CM mode, send the original value
        log(`Sending original value to cart: ${displayedValue}`);
      }
      
      // Force a small delay to ensure the form data is updated
      setTimeout(() => {
        log('Form submission proceeding...');
      }, 10);
    }
  }

  // Add form submission listener
  document.addEventListener('submit', handleFormSubmission);

  // Hook into variant updates
  hookIntoVariantUpdates();

  // Continuous monitoring to ensure toggle stays visible
  function monitorToggleVisibility() {
    const scopes = document.querySelectorAll('product-variant-selects');
    
    scopes.forEach(scope => {
      const toggle = scope.querySelector('[data-unit-toggle]');
      const firstFieldset = scope.querySelector('fieldset');
      const storedUnit = localStorage.getItem('size-unit-preference') || 'cm';
      const currentUnit = scope.getAttribute('data-active-unit');
      
      // If fieldset exists but toggle doesn't, recreate it
      if (firstFieldset && !toggle) {
        log('Toggle missing, recreating...');
        const newToggle = createToggle(scope);
        if (newToggle) {
          // Store original sizes first
          storeOriginalSizes(scope);
          // Then set the active unit and convert
          setActiveUnit(scope, storedUnit);
          convertSizesToUnit(scope, storedUnit);
          log(`Recreated toggle and converted sizes to: ${storedUnit}`);
        }
      } else if (toggle && currentUnit !== storedUnit) {
        // Toggle exists but unit doesn't match stored preference - fix it
        log(`Unit mismatch - current: ${currentUnit}, stored: ${storedUnit}, fixing...`);
        setActiveUnit(scope, storedUnit);
        convertSizesToUnit(scope, storedUnit);
      }
    });
  }

  // Run monitoring every 500ms
  setInterval(monitorToggleVisibility, 500);

  // Function to update cart display with converted sizes
  function updateCartDisplay() {
    if (!window.lastCartConversion) return;
    
    const { original, converted } = window.lastCartConversion;
    
    // Find cart items and update the size display
    const cartItems = document.querySelectorAll('.cart-item__content dl dd');
    cartItems.forEach(dd => {
      if (dd.textContent.trim() === original) {
        dd.textContent = converted;
        log(`Updated cart display: ${original} -> ${converted}`);
      }
    });
    
    // Also check for size in cart item properties
    const cartProperties = document.querySelectorAll('.cart-item__properties dd');
    cartProperties.forEach(dd => {
      if (dd.textContent.trim() === original) {
        dd.textContent = converted;
        log(`Updated cart properties: ${original} -> ${converted}`);
      }
    });
  }

  // Monitor for cart updates
  const cartObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Check if cart items were added
        const hasCartItems = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === Node.ELEMENT_NODE && 
          (node.classList?.contains('cart-item') || node.querySelector?.('.cart-item'))
        );
        
        if (hasCartItems) {
          setTimeout(updateCartDisplay, 100);
        }
      }
    });
  });

  // Start observing cart changes
  const cartContainer = document.querySelector('.cart-drawer, .cart-popup, [data-cart-drawer]');
  if (cartContainer) {
    cartObserver.observe(cartContainer, { childList: true, subtree: true });
  }

  // Also observe document body for cart updates
  cartObserver.observe(document.body, { childList: true, subtree: true });

  // Expose for debugging
  window.SizeUnits = {
    init,
    reinitializeAfterAjax,
    convertSizesToUnit,
    storeOriginalSizes,
    monitorToggleVisibility
  };

})();